/**
 * Env vars. Валидируем Zod'ом при загрузке — если что-то не задано,
 * приложение падает сразу с понятным сообщением в консоли, а не
 * мистически в рантайме.
 */

import {z} from 'zod';

/**
 * Страна инстанса. Определяет бренд, валюту, языки, домен публичного
 * сайта и заголовок `Country-Code` в запросах к API.
 */
export type CountryCode = 'uz' | 'kg' | 'ru' | 'kz' | 'tj';

const EnvSchema = z.object({
  /**
   * Базовый адрес API. Допустимы два варианта:
   *  - абсолютный URL: `https://gateway.kassir.kg` → бьёмся напрямую
   *  - относительный:  `/api`                       → через Vite dev-proxy
   */
  VITE_API_BASE_URL: z.string().min(1).default('https://gateway.kassir.kg'),
  /**
   * Nest-панель рядом с основным API (`https://panel.showgo.kz/api`).
   * Отдельный бэк со своими настройками публичного сайта — управление
   * видимостью блоков в карточке сеанса.
   *
   * Пусто = панель не поднята, пункт «Система → Настройки сеанса» скрыт.
   */
  VITE_NEST_API_URL: z.string().default(''),

  VITE_COUNTRY_CODE: z.enum(['uz', 'kg', 'ru', 'kz', 'tj']).default('kg'),
  VITE_APP_VERSION: z.string().default('0.1.0'),

  /**
   * Опциональный таргет для Vite dev-proxy. Срабатывает когда
   * VITE_API_BASE_URL === '/api'. См. vite.config.ts.
   */
  VITE_DEV_PROXY_TARGET: z.string().default(''),

  /**
   * Включает MSW в dev для работы поверх моков (если установлен).
   * В шаге 1 MSW ещё не подключен — поле опциональное.
   */
  VITE_ENABLE_MSW: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),

  /**
   * Sentry — DSN и environment. Пусто в dev, заполняется в prod-сборке.
   */
  VITE_SENTRY_DSN: z.string().default(''),
  VITE_SENTRY_ENVIRONMENT: z.string().default('development'),

  /**
   * Dev-only: автозаполнение формы логина. В prod build игнорируется.
   */
  VITE_DEV_EMAIL: z.string().default(''),
  VITE_DEV_PASSWORD: z.string().default(''),
});

const parsed = EnvSchema.safeParse(import.meta.env);

if (!parsed.success) {
  console.error('[env] invalid env vars', parsed.error.flatten().fieldErrors);
  throw new Error('Invalid environment configuration. See console.');
}

export const env = parsed.data;
